/**
 * Hook to map Encompass LE fields to ParsedLoanEstimate format
 *
 * Maps live Encompass field IDs (LE1.X*, LE2.X*, LE3.X*) to the
 * ParsedLoanEstimate interface for display in the viewer.
 */

import { useMemo } from 'react';
import type {
  ParsedLoanEstimate,
  ParseResult,
  ProjectedPaymentPeriod,
  ClosingCostsSections,
  CashToCloseSection,
  ComparisonsSection,
  LoanOfficerInfo,
  TaxesInsuranceInfo,
  FeeItem,
  EncompassFee
} from './types';
import { getFeeSection, getFeeLabel } from './fee-type-mapping';

/**
 * Fee line item as stored in le_fees JSONB (new structure from fieldReader API)
 */
interface FeeLineItem {
  name: string;
  section: 'A' | 'B' | 'C' | 'E' | 'F' | 'G' | 'H';
  borrowerAmount: number;
  sellerAmount: number;
  paidTo: string;
  paidToType?: string;    // Lender, Broker, Other, etc.
  totalAmount: number;
  months?: number;
  perMonth?: number;
  aprAffecting?: boolean; // Y/N from Encompass "Impacts APR" checkbox
  // Loan Origination Fee borrower payment breakdown
  originationPercent?: number;
  borrowerFinanced?: number;
  borrowerPTC?: number;
  borrowerAtClosing?: number;
  borrowerOutsideClosing?: number;
  borrowerTotalPaid?: number;
}

interface LeFieldsLoan {
  le_fields?: Record<string, string | number | null>;
  le_fees?: EncompassFee[] | FeeLineItem[];
  borrower_first_name?: string;
  borrower_last_name?: string;
  property_address?: string;
  subject_property_city?: string;
  subject_property_state?: string;
  property_zip?: string;
  property_value?: number;
  trans_details_loan_number?: string;
  trans_details_loan_amt?: number;
  trans_details_interest_rate?: number;
  trans_details_loan_purpose?: string;
  loan_officer_first_name?: string;
  loan_officer_last_name?: string;
  [key: string]: any;
}

/**
 * Parse a numeric value from Encompass field
 */
function parseNumber(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === '') {
return 0;
}
  if (typeof value === 'number') {
return value;
}
  // Remove currency symbols and commas
  const cleaned = String(value).replace(/[$,%]/g, '').replace(/,/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Get a string value from Encompass field
 */
function getString(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
return '';
}
  return String(value);
}

/**
 * Check if a boolean indicator is "yes" or similar
 */
function isYes(value: string | number | null | undefined): boolean {
  if (!value) {
return false;
}
  const str = String(value).toLowerCase();
  return str === 'yes' || str === 'y' || str === 'true' || str === '1';
}

/**
 * Extract the fee amount from various Encompass fee fields
 * Encompass stores amounts in different fields depending on fee type
 */
function extractFeeAmount(fee: EncompassFee): number {
  // Primary: borPaidAmount (most common)
  if (fee.borPaidAmount && fee.borPaidAmount > 0) {
    return fee.borPaidAmount;
  }

  // Alternative: newHudBorPaidAmount (string, used by some fees like ClosingFee)
  if (fee.newHudBorPaidAmount) {
    const parsed = parseFloat(String(fee.newHudBorPaidAmount).replace(/[$,]/g, ''));
    if (!isNaN(parsed) && parsed > 0) {
return parsed;
}
  }

  // For prepaids with monthly payment: calculate total
  if (fee.monthlyPayment && fee.numberOfMonths) {
    const total = fee.monthlyPayment * fee.numberOfMonths;
    if (total > 0) {
return total;
}
  }

  // Fallback: paidToOthers (sometimes has amount when borPaidAmount doesn't)
  if (fee.paidToOthers && fee.paidToOthers > 0) {
    return fee.paidToOthers;
  }

  // Other paid-by fields
  if (fee.selPaidAmount && fee.selPaidAmount > 0) {
return fee.selPaidAmount;
}
  if (fee.lenderPaidAmount && fee.lenderPaidAmount > 0) {
return fee.lenderPaidAmount;
}
  if (fee.otherPaidAmount && fee.otherPaidAmount > 0) {
return fee.otherPaidAmount;
}

  return 0;
}

/**
 * Type guard to check if a fee is in the new FeeLineItem format
 */
function isFeeLineItem(fee: EncompassFee | FeeLineItem): fee is FeeLineItem {
  return 'section' in fee && 'borrowerAmount' in fee;
}

/**
 * Map new-format FeeLineItem fees to TRID sections
 */
function mapFeeLineItemsToSections(fees: FeeLineItem[]): Record<string, FeeItem[]> {
  const sections: Record<string, FeeItem[]> = {
    A: [], B: [], C: [], E: [], F: [], G: [], H: []
  };

  for (const fee of fees) {
    const amount = fee.borrowerAmount;

    // Skip fees with no borrower amount
    if (amount === 0) {
continue;
}

    // Keep fee name and paidTo as separate fields
    const feeName = fee.name;
    const paidTo = fee.paidTo || undefined;
    // Build combined description only for legacy display
    const description = paidTo ? `${feeName} paid to ${paidTo}` : feeName;

    // Build detail string for escrow items only
    let detail: string | undefined;
    if (fee.months && fee.perMonth) {
      detail = `$${fee.perMonth.toFixed(2)}/mo × ${fee.months} months`;
    }

    // Determine who paid
    let paidBy: FeeItem['paidBy'] = 'Borrower';
    if (fee.sellerAmount > fee.borrowerAmount) {
      paidBy = 'Seller';
    }

    sections[fee.section].push({
      description,
      feeName,
      paidTo,
      amount,
      detail,
      paidBy,
      paidToType: fee.paidToType,
      aprAffecting: fee.aprAffecting,
      originationPercent: fee.originationPercent,
      borrowerFinanced: fee.borrowerFinanced,
      borrowerPTC: fee.borrowerPTC,
      borrowerAtClosing: fee.borrowerAtClosing,
      borrowerOutsideClosing: fee.borrowerOutsideClosing,
      borrowerTotalPaid: fee.borrowerTotalPaid
    });
  }

  // Sort each section by amount (highest first)
  for (const section of Object.keys(sections)) {
    sections[section].sort((a, b) => b.amount - a.amount);
  }

  return sections;
}

/**
 * Map old-format EncompassFee fees to TRID sections (for backward compatibility)
 */
function mapEncompassFeesToSections(fees: EncompassFee[]): Record<string, FeeItem[]> {
  const sections: Record<string, FeeItem[]> = {
    A: [], B: [], C: [], E: [], F: [], G: [], H: []
  };

  for (const fee of fees) {
    const section = getFeeSection(fee.feeType);
    const description = getFeeLabel(fee.feeType, fee.description || fee.feeDescription || fee.paidToName);

    // Extract amount from various possible fields
    const amount = extractFeeAmount(fee);

    // Skip fees with no amount
    if (amount === 0) {
continue;
}

    // Determine who paid
    let paidBy: FeeItem['paidBy'] = 'Borrower';
    if (fee.selPaidAmount && fee.selPaidAmount > 0) {
paidBy = 'Seller';
} else if (fee.lenderPaidAmount && fee.lenderPaidAmount > 0) {
paidBy = 'Lender';
} else if (fee.otherPaidAmount && fee.otherPaidAmount > 0) {
paidBy = 'Other';
} else if (fee.ptb) {
      const ptbLower = fee.ptb.toLowerCase();
      if (ptbLower.includes('seller')) {
paidBy = 'Seller';
} else if (ptbLower.includes('lender')) {
paidBy = 'Lender';
} else if (ptbLower.includes('other')) {
paidBy = 'Other';
}
    }

    // Build detail string for special fees
    let detail: string | undefined;
    if (fee.feeType === 'PrepaidInterest' && fee.days && fee.amountPerDay) {
      detail = `$${fee.amountPerDay.toFixed(2)} per day for ${fee.days} days`;
    } else if (fee.monthlyPayment && fee.numberOfMonths) {
      // Checked before the bare numberOfMonths branch — the richer detail
      // was unreachable when this sat after it.
      detail = `$${fee.monthlyPayment.toFixed(2)}/mo × ${fee.numberOfMonths} months`;
    } else if (fee.numberOfMonths) {
      detail = `${fee.numberOfMonths} months`;
    } else if (fee.percentOfLoan) {
      detail = `${fee.percentOfLoan}% of loan amount`;
    }

    sections[section].push({
      description,
      amount,
      detail,
      paidBy
    });
  }

  // Sort each section by amount (highest first)
  for (const section of Object.keys(sections)) {
    sections[section].sort((a, b) => b.amount - a.amount);
  }

  return sections;
}

/**
 * Map fees to TRID sections (handles both old and new formats)
 */
function mapFeesToSections(fees: EncompassFee[] | FeeLineItem[]): Record<string, FeeItem[]> {
  if (!fees || fees.length === 0) {
    return { A: [], B: [], C: [], E: [], F: [], G: [], H: [] };
  }

  // Check first fee to determine format
  const firstFee = fees[0];
  if (isFeeLineItem(firstFee)) {
    return mapFeeLineItemsToSections(fees as FeeLineItem[]);
  } else {
    return mapEncompassFeesToSections(fees as EncompassFee[]);
  }
}

/**
 * Map LE fields to ParsedLoanEstimate format
 */
export function useLeFieldsMapper(loan: LeFieldsLoan | null | undefined): ParseResult {
  return useMemo(() => {
    if (!loan?.le_fields || Object.keys(loan.le_fields).length === 0) {
      return {
        success: false,
        data: null,
        error: 'No LE fields data available',
        warnings: []
      };
    }

    const f = loan.le_fields;
    const warnings: string[] = [];

    // Build projected payments array
    const projectedPayments: ProjectedPaymentPeriod[] = [];

    // Column 1 (typically Years 1-X for IO loans or full term for fixed)
    const col1Year = getString(f['LE1.X41']);
    if (col1Year || f['LE1.X47']) {
      projectedPayments.push({
        label: col1Year || 'Years 1-30',
        principalInterest: parseNumber(f['LE1.X42']) || parseNumber(f['LE1.X43']),
        mortgageInsurance: parseNumber(f['LE1.X45']),
        escrow: parseNumber(f['LE1.X46']),
        totalMonthly: parseNumber(f['LE1.X47']) || parseNumber(f['LE1.X48']),
        isInterestOnly: isYes(f['LE1.X44'])
      });
    }

    // Column 2 (for loans with payment changes)
    const col2From = getString(f['LE1.X49']);
    const col2To = getString(f['LE1.X50']);
    if (col2From || col2To || f['LE1.X56']) {
      projectedPayments.push({
        label: col2From && col2To ? `Years ${col2From}-${col2To}` : 'Years 11-30',
        principalInterest: parseNumber(f['LE1.X51']) || parseNumber(f['LE1.X52']),
        mortgageInsurance: parseNumber(f['LE1.X54']),
        escrow: parseNumber(f['LE1.X55']),
        totalMonthly: parseNumber(f['LE1.X56']) || parseNumber(f['LE1.X57']),
        isInterestOnly: isYes(f['LE1.X53'])
      });
    }

    // Column 3 (for complex ARM/IO loans)
    const col3From = getString(f['LE1.X58']);
    const col3To = getString(f['LE1.X59']);
    if (col3From || col3To || f['LE1.X65']) {
      projectedPayments.push({
        label: col3From && col3To ? `Years ${col3From}-${col3To}` : 'Years',
        principalInterest: parseNumber(f['LE1.X60']) || parseNumber(f['LE1.X61']),
        mortgageInsurance: parseNumber(f['LE1.X63']),
        escrow: parseNumber(f['LE1.X64']),
        totalMonthly: parseNumber(f['LE1.X65']) || parseNumber(f['LE1.X66']),
        isInterestOnly: isYes(f['LE1.X62'])
      });
    }

    // Default if no payments found
    if (projectedPayments.length === 0) {
      projectedPayments.push({
        label: 'Years 1-30',
        principalInterest: parseNumber(f['5']),
        mortgageInsurance: 0,
        escrow: parseNumber(f['LE1.X29']),
        totalMonthly: parseNumber(f['5']) + parseNumber(f['LE1.X29'])
      });
    }

    // Taxes, Insurance & Assessments
    const taxesInsuranceAssessments: TaxesInsuranceInfo = {
      monthlyAmount: parseNumber(f['LE1.X29']),
      includesPropertyTaxes: isYes(f['LE1.X30']),
      includesHomeownersInsurance: isYes(f['LE1.X31']),
      includesOther: isYes(f['LE1.X32']),
      otherDescription: null,
      inEscrow: {
        propertyTaxes: isYes(f['LE1.X30']),
        homeownersInsurance: isYes(f['LE1.X31'])
      }
    };

    // Map fees to sections if available
    const feesBySection = loan.le_fees && loan.le_fees.length > 0
      ? mapFeesToSections(loan.le_fees)
      : { A: [], B: [], C: [], E: [], F: [], G: [], H: [] };

    // Calculate section totals from items
    const sectionATotal = feesBySection.A.reduce((sum, item) => sum + (item.amount || 0), 0);
    const sectionAWithoutDiscount = feesBySection.A
      .filter(item => item.feeName !== 'Loan Discount' && item.description !== 'Loan Discount')
      .reduce((sum, item) => sum + (item.amount || 0), 0);
    const sectionBTotal = feesBySection.B.reduce((sum, item) => sum + (item.amount || 0), 0);
    const sectionCTotal = feesBySection.C.reduce((sum, item) => sum + (item.amount || 0), 0);
    const sectionETotal = feesBySection.E.reduce((sum, item) => sum + (item.amount || 0), 0);
    const sectionFTotal = feesBySection.F.reduce((sum, item) => sum + (item.amount || 0), 0);
    const sectionGTotal = feesBySection.G.reduce((sum, item) => sum + (item.amount || 0), 0);
    const sectionHTotal = feesBySection.H.reduce((sum, item) => sum + (item.amount || 0), 0);

    // Calculate aggregate totals
    const totalLoanCosts = sectionATotal + sectionBTotal + sectionCTotal;
    const totalOtherCosts = sectionETotal + sectionFTotal + sectionGTotal + sectionHTotal;
    const lenderCredits = parseNumber(f['LE2.XLC']);
    const totalClosingCosts = totalLoanCosts + totalOtherCosts - lenderCredits;

    const closingCosts: ClosingCostsSections = {
      sectionA: {
        label: 'A. Origination Charges',
        // Display full total (including Loan Discount)
        total: sectionATotal,
        items: feesBySection.A,
        // Encompass NEWHUD.X686 excludes Loan Discount (covers lines 802-804, not 801)
        // Validation compares (total - Loan Discount) to this value
        encompassTotal: parseNumber(f['NEWHUD.X686']),
        // Store the amount without discount for validation
        totalWithoutDiscount: sectionAWithoutDiscount
      },
      sectionB: {
        label: 'B. Services You Cannot Shop For',
        // Calculate total from items
        total: sectionBTotal,
        items: feesBySection.B
        // Note: Section B validation is done at A+B level using encompassTotalAB
      },
      sectionC: {
        label: 'C. Services You Can Shop For',
        // Calculate total from items
        total: sectionCTotal,
        items: feesBySection.C,
        // Encompass value for validation comparison (NEWHUD2.X40 = total of lines 1101-1016)
        encompassTotal: parseNumber(f['NEWHUD2.X40'])
      },
      totalLoanCosts,
      sectionE: {
        label: 'E. Taxes and Other Government Fees',
        // Calculate total from items
        total: sectionETotal,
        items: feesBySection.E,
        // Encompass value for validation comparison (NEWHUD2.X44)
        encompassTotal: parseNumber(f['NEWHUD2.X44'])
      },
      sectionF: {
        label: 'F. Prepaids',
        total: sectionFTotal,
        items: feesBySection.F,
        // Encompass value for validation comparison (NEWHUD2.X32 = total of lines 901-912)
        encompassTotal: parseNumber(f['NEWHUD2.X32'])
      },
      sectionG: {
        label: 'G. Initial Escrow Payment at Closing',
        // Calculate total from items
        total: sectionGTotal,
        items: feesBySection.G,
        // Encompass value for validation comparison
        encompassTotal: parseNumber(f['NEWHUD.X1719'])
      },
      sectionH: {
        label: 'H. Other',
        total: sectionHTotal,
        items: feesBySection.H
      },
      totalOtherCosts,
      lenderCredits,
      totalClosingCosts,
      // Encompass value for Total Closing Costs validation (NEWHUD.X277 = borrower settlement charges)
      encompassTotalClosingCosts: parseNumber(f['NEWHUD.X277']),
      // Encompass value for A+B combined validation (NEWHUD2.X28)
      encompassTotalAB: parseNumber(f['NEWHUD2.X28'])
    };

    // Cash to Close — HUD/1003 settlement statement fields
    // These are live transactional fields that update through closing
    const loanPurpose = getString(f['LE1.X4']) || getString(f['19']) || loan.trans_details_loan_purpose || '';
    const isPurchase = /purchase/i.test(loanPurpose);

    const cashToClose: CashToCloseSection = {
      // Costs
      purchasePrice: parseNumber(f['136']),
      loanAmount: parseNumber(f['1109']) || parseNumber(f['2']),
      estimatedClosingCosts: parseNumber(f['137']),
      estimatedPrepaids: parseNumber(f['139']),
      mipFundingFee: parseNumber(f['969']),
      discountPoints: parseNumber(f['1093']),
      totalCosts: parseNumber(f['1073']),
      // Credits
      sellerPaidCosts: parseNumber(f['143']),
      otherCredits: parseNumber(f['URLA.X149']),
      lenderBrokerOtherPaid: parseNumber(f['1852']),
      lenderPaidCC: parseNumber(f['LENPCC']),
      brokerPaidCC: parseNumber(f['BKRPCC']),
      otherPaidCC: parseNumber(f['OTHPCC']),
      lenderCredit: parseNumber(f['4794']),
      sellerCredit: parseNumber(f['4795']),
      otherAssets: parseNumber(f['URLA.X151']),
      refundedMIP: parseNumber(f['1134']),
      deposit: parseNumber(f['L128']),
      additionalCredits: [
        parseNumber(f['141']),
        parseNumber(f['1095']),
        parseNumber(f['1115']),
        parseNumber(f['1647'])
      ],
      piggybackClosingCost: parseNumber(f['1851']),
      totalLoanCredits: parseNumber(f['URLA.X152']),
      totalCredits: parseNumber(f['1844']),
      // Result
      cashToClose: parseNumber(f['142']),
      isPurchase
    };

    // Comparisons
    const comparisons: ComparisonsSection = {
      apr: parseNumber(f['799']),
      tip: parseNumber(f['LE3.X16']),
      fiveYearTotal: parseNumber(f['LE3.X17']),
      fiveYearPrincipal: parseNumber(f['LE3.X18'])
    };

    // Loan Officer
    const loanOfficer: LoanOfficerInfo = {
      name: getString(f['LE3.X4']) || `${loan.loan_officer_first_name || ''} ${loan.loan_officer_last_name || ''}`.trim(),
      nmls: getString(f['LE3.X5']),
      licenseId: getString(f['LE3.X24']) || null,
      email: getString(f['LE3.X2']),
      phone: getString(f['LE3.X3'])
    };

    // Build full property address
    const propertyParts = [
      loan.property_address,
      loan.subject_property_city,
      loan.subject_property_state,
      loan.property_zip
    ].filter(Boolean);
    const propertyAddress = propertyParts.join(', ') || 'Property Address';

    // Loan amount and rate - prefer le_fields, fall back to loan fields
    const loanAmount = parseNumber(f['1109']) || parseNumber(f['2']) || loan.trans_details_loan_amt || 0;
    const interestRate = parseNumber(f['3']) || loan.trans_details_interest_rate || 0;

    // Build ParsedLoanEstimate
    const data: ParsedLoanEstimate = {
      // Header
      lenderName: '',
      lenderAddress: '',
      dateIssued: getString(f['LE1.X1']),
      loanId: getString(f['364']) || loan.trans_details_loan_number || '',

      // Applicant/Property
      borrowerName: `${loan.borrower_first_name || ''} ${loan.borrower_last_name || ''}`.trim() || 'Borrower',
      borrowerAddress: '',
      coborrowerName: null,
      propertyAddress,
      estimatedPropertyValue: loan.property_value || 0,

      // Loan Terms
      loanAmount,
      interestRate,
      monthlyPI: parseNumber(f['5']),
      loanTerm: getString(f['LE1.X2']) || getString(f['4']) ? `${getString(f['LE1.X2']) || Math.round(parseNumber(f['4']) / 12)} years` : '30 years',
      loanPurpose: getString(f['LE1.X4']) || getString(f['19']) || loan.trans_details_loan_purpose || '',
      loanProduct: getString(f['LE1.X5']) || '',
      loanType: '',
      rateLock: {
        locked: false,
        until: null,
        expirationDate: null
      },
      prepaymentPenalty: !!getString(f['LE1.X27']),
      balloonPayment: false,

      // Can increase flags
      canIncrease: {
        loanAmount: getString(f['LE1.X10']).toLowerCase().includes('can'),
        interestRate: getString(f['LE1.X17']).toLowerCase().includes('can'),
        monthlyPI: getString(f['LE1.X23']).toLowerCase().includes('can'),
        monthlyPIDetails: getString(f['LE1.X19']) || null
      },

      // Projected Payments
      projectedPayments,
      taxesInsuranceAssessments,

      // Costs at Closing summary
      estimatedClosingCosts: parseNumber(f['LE2.XSTJ']),
      estimatedCashToClose: parseNumber(f['LE1.X87']) || cashToCloseAmount,
      closingCostsBreakdown: `$${closingCosts.totalLoanCosts.toLocaleString()} Loan Costs + $${closingCosts.totalOtherCosts.toLocaleString()} Other Costs - $${closingCosts.lenderCredits.toLocaleString()} Credits`,

      // Closing Costs (Sections A-J)
      closingCosts,

      // Cash to Close
      cashToClose,

      // Comparisons
      comparisons,

      // Loan Officer
      loanOfficer,

      // Mortgage Broker (optional)
      mortgageBroker: getString(f['LE3.X6']) ? {
        companyName: '',
        nmls: getString(f['LE3.X6']),
        licenseId: getString(f['LE3.X22']) || null,
        loanOfficerName: getString(f['LE3.X9']) || null,
        loanOfficerNmls: getString(f['LE3.X10']) || null,
        email: getString(f['LE3.X7']) || null,
        phone: getString(f['LE3.X8']) || null
      } : null,

      // Other Considerations
      otherConsiderations: {
        appraisal: getString(f['LE3.X11']) || 'An appraisal may be required',
        assumption: getString(f['LE3.X12']).toLowerCase().includes('not') ? 'not_allowed' : 'allowed',
        homeownersInsurance: getString(f['LE3.X13']) || 'Required',
        latePayment: '',
        liabilityAfterForeclosure: null,
        refinance: '',
        servicing: getString(f['LE3.X14']).toLowerCase().includes('transfer') ? 'transfer' : 'service'
      }
    };

    return {
      success: true,
      data,
      error: null,
      warnings
    };
  }, [loan]);
}

export default useLeFieldsMapper;
